import { Charge } from "@/pages/sy/models/Charge";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import {
  LAYER_MOVE_MODE,
  type LayerMoveMode,
} from "@/pages/sy/models/LayerMoveMode";
import { isInSharedLayer } from "@/pages/sy/utils/layerGroups";
import {
  useUpsertChargeLayerMutation,
  useDeleteChargeLayerMutation,
  useReorderChargeLayerMutation,
  useSetCurrentChargeLayerMutation,
  useUnsetCurrentChargeLayerMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";

type UseLayerHandlersProps = {
  isEditMode: boolean;
  currentOperatorId: number | null;
  chargeLayers: ChargeLayer[];
  selectedLayer: ChargeLayer | null;
  setSelectedLayer: (layer: ChargeLayer | null) => void;
  charge: Charge | null;
  setIsLayerModalOpen: (isLayerModalOpen: boolean) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
};

export const useLayerHandlers = ({
  isEditMode,
  chargeLayers,
  selectedLayer,
  setSelectedLayer,
  charge,
  setIsLayerModalOpen,
  setError,
  setNotificationMessage,
}: UseLayerHandlersProps) => {
  // Mutations
  const upsertLayerMutation = useUpsertChargeLayerMutation();
  const deleteLayerMutation = useDeleteChargeLayerMutation();
  const reorderLayerMutation = useReorderChargeLayerMutation();
  const setCurrentLayerMutation = useSetCurrentChargeLayerMutation();
  const unsetCurrentLayerMutation = useUnsetCurrentChargeLayerMutation();

  // Combined loading state for UI
  const isLayerMutating =
    upsertLayerMutation.isPending ||
    deleteLayerMutation.isPending ||
    reorderLayerMutation.isPending ||
    setCurrentLayerMutation.isPending ||
    unsetCurrentLayerMutation.isPending;

  const handleLayerSelect = (layer: ChargeLayer) => {
    if (charge?.ChargeStatusCode !== "O") {
      return;
    }
    if (!isEditMode && layer.IsCurrent) {
      setSelectedLayer(null);
      handleUnsetCurrentLayerClick(layer.ChargeLayerId as number);
    } else {
      if (isEditMode) {
        setSelectedLayer(
          selectedLayer?.ChargeLayerId === layer.ChargeLayerId ? null : layer,
        );
        setIsLayerModalOpen(true);
      } else {
        handleSetCurrentLayerClick(layer.ChargeLayerId as number);
      }
    }
  };

  const handleSetCurrentLayerClick = (layerId: number) => {
    if (!charge?.ChargeId || isEditMode) return;
    if (charge.ChargeStatusCode === "H") return;

    setCurrentLayerMutation.mutate(layerId, {
      onError: (error) => {
        setError(
          "Error setting current layer: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleUnsetCurrentLayerClick = (layerId: number) => {
    if (!charge?.ChargeId || isEditMode) return;
    if (charge.ChargeStatusCode === "H") return;

    unsetCurrentLayerMutation.mutate(layerId, {
      onError: (error) => {
        setError(
          "Error unsetting current layer: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleAddNewLayer = () => {
    if (!charge?.ChargeId || !isEditMode) return;
    setSelectedLayer(null);
    setIsLayerModalOpen(true);
  };

  const handleCloseLayerModal = () => {
    setIsLayerModalOpen(false);
    setSelectedLayer(null);
  };

  // Complex operation with multiple sequential steps - uses mutateAsync
  const handleSaveLayer = async (data: {
    commodityId: number | null;
    weight: number;
    layerNumber?: number;
    setAsActive?: boolean;
    placementMode?: LayerMoveMode | null;
  }) => {
    if (!charge?.ChargeId) return;

    try {
      // Step 1: Create or update the layer
      const layerToUpdate = {
        ChargeId: charge.ChargeId,
        CommodityId: data.commodityId,
        Weight: data.weight,
        IsWeightManual: data.weight !== 0,
        ...(selectedLayer
          ? { ChargeLayerId: selectedLayer.ChargeLayerId }
          : {
              LayerNumber:
                data.placementMode === LAYER_MOVE_MODE.REORDER
                  ? null
                  : data.layerNumber,
            }),
      };

      const result = await upsertLayerMutation.mutateAsync(layerToUpdate);

      const layerId =
        selectedLayer?.ChargeLayerId ||
        result.data?.tables?.[0]?.rows?.[0]?.ChargeLayerId;

      if (!layerId) {
        setError("Failed to get layer ID after save");
        return;
      }

      // Step 2: Reorder layer if needed
      if (data.layerNumber) {
        const shouldReorder = selectedLayer
          ? data.layerNumber !== selectedLayer.LayerNumber
          : data.placementMode === LAYER_MOVE_MODE.REORDER;

        if (shouldReorder) {
          try {
            await reorderLayerMutation.mutateAsync({
              chargeLayerId: layerId,
              newLayerNumber: data.layerNumber,
              mode: data.placementMode ?? LAYER_MOVE_MODE.REORDER,
            });
          } catch (err) {
            setError(
              "Failed to reorder layer: " +
                (err instanceof Error ? err.message : String(err)),
            );
          }
        }
      }

      // Step 3: Handle setAsActive toggle
      if (data.setAsActive !== undefined && data.setAsActive !== null) {
        const wasActive = selectedLayer?.IsCurrent === true;
        const shouldBeActive = data.setAsActive === true;

        const shouldMakeApiCall = selectedLayer
          ? true
          : wasActive !== shouldBeActive;

        if (shouldMakeApiCall) {
          try {
            if (shouldBeActive) {
              await setCurrentLayerMutation.mutateAsync(layerId);
            } else {
              await unsetCurrentLayerMutation.mutateAsync(layerId);
            }
          } catch (err) {
            setError(
              "Failed to update layer active status: " +
                (err instanceof Error ? err.message : String(err)),
            );
          }
        }
      }

      if (selectedLayer) {
        setNotificationMessage("Layer updated successfully");
      }
      setSelectedLayer(null);
    } catch (err) {
      setError(
        "Error saving layer: " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  };

  const handleDeleteLayer = (layer: ChargeLayer) => {
    if (!layer.ChargeLayerId) return;

    const isSharedMaterial = isInSharedLayer(
      chargeLayers,
      layer,
      "ChargeLayerId",
    );

    deleteLayerMutation.mutate(layer.ChargeLayerId, {
      onSuccess: () => {
        setNotificationMessage(
          isSharedMaterial
            ? `${layer.CommodityName ?? "Material"} removed from layer ${layer.LayerNumber}`
            : "Layer deleted successfully",
        );
        setSelectedLayer(null);
      },
      onError: (error) => {
        setError(
          "Error deleting layer: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  return {
    handleLayerSelect,
    handleAddNewLayer,
    handleCloseLayerModal,
    handleSaveLayer,
    handleDeleteLayer,
    isLayerMutating,
  };
};
